import { useState, useEffect, createContext, useContext } from 'react';
import { getRuntimeConfig } from '../config';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: string | null;
  user: { sub: string; email: string; role: string; domains: string[] } | null;
  token: string | null;
  login: () => void;
  logout: () => void;
  /** Re-reads localStorage and updates React state -- CallbackPage
   * calls this right after completeSigninRedirect() writes the new
   * session, since that function is a plain async helper with no
   * access to this provider's setState calls. */
  refreshSession: () => void;
  setAuthError: (message: string | null) => void;
}

const AuthContext = createContext<AuthState>({
  isAuthenticated: false,
  isLoading: true,
  authError: null,
  user: null,
  token: null,
  login: () => {},
  logout: () => {},
  refreshSession: () => {},
  setAuthError: () => {},
});

/** ZTrust Keycloak is the sole identity provider -- Cognito and Amplify
 * are removed entirely (see auth-stack.ts and
 * lambdas/ztrust_authorizer/handler.py for the full rationale).
 *
 * PKCE is hand-rolled here rather than via oidc-client-ts: that
 * library has no clean public API to route ONLY the code-to-token
 * exchange through a custom backend endpoint while keeping its own
 * PKCE/state management, and this app's real production incident
 * requires exactly that split -- auth.gdna.io's Keycloak /token
 * endpoint does not return Access-Control-Allow-Origin on ANY
 * response for the gdna-manamind client (confirmed directly via curl,
 * independent of client-auth type, request shape, or HTTP status;
 * Keycloak's own Events log shows real logins succeeding server-side,
 * but the browser can never read that response). The redirect to
 * Keycloak's /auth endpoint and the JWKS fetch (used by
 * ztrust_authorizer, not this file) are unaffected -- both
 * independently confirmed to return correct CORS headers already --
 * so only the token exchange itself needs to move server-to-server,
 * via lambdas/auth_token_proxy. PKCE (RFC 7636) itself is genuinely
 * small: a random code_verifier, its SHA-256 digest base64url-encoded
 * as the code_challenge. No library needed for that half.
 */
const STORAGE_KEY_PKCE = 'manamind_pkce_state';
const STORAGE_KEY_SESSION = 'manamind_session';

interface StoredSession {
  access_token: string;
  refresh_token?: string;
  expires_at: number; // epoch seconds
  claims: { sub: string; email: string; role: string; domains: string[] };
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Base64Url(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return base64UrlEncode(new Uint8Array(digest));
}

/** Decodes a JWT's payload without verifying its signature -- safe
 * here ONLY because this token was just received directly from
 * auth_token_proxy, which got it directly from Keycloak's own /token
 * endpoint over a server-to-server HTTPS call; this is display/claims
 * extraction for the UI, not an authorization decision. Every actual
 * authorization decision (API access) is enforced server-side by
 * ztrust_authorizer, which DOES verify the signature -- see that
 * Lambda's own handler.py. */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const payload = token.split('.')[1] ?? '';
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  return JSON.parse(atob(padded));
}

function claimsFromAccessToken(accessToken: string): StoredSession['claims'] {
  const payload = decodeJwtPayload(accessToken);
  return {
    sub: (payload.sub as string) || '',
    email: (payload.email as string) || '',
    // Keycloak's own claim names (manamind_role / manamind_domains) --
    // read directly here, matching ztrust_authorizer's server-side
    // claim reconstruction, since the frontend talks to Keycloak
    // directly (via the proxy) rather than through any re-mapping layer.
    role: (payload.manamind_role as string) || 'viewer',
    domains: Array.isArray(payload.manamind_domains) ? (payload.manamind_domains as string[]) : [],
  };
}

function loadStoredSession(): StoredSession | null {
  const raw = localStorage.getItem(STORAGE_KEY_SESSION);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as StoredSession;
    if (session.expires_at * 1000 < Date.now()) {
      localStorage.removeItem(STORAGE_KEY_SESSION);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

function storeSession(tokenResponse: { access_token: string; refresh_token?: string; expires_in?: number }): StoredSession {
  const session: StoredSession = {
    access_token: tokenResponse.access_token,
    refresh_token: tokenResponse.refresh_token,
    expires_at: Math.floor(Date.now() / 1000) + (tokenResponse.expires_in ?? 300),
    claims: claimsFromAccessToken(tokenResponse.access_token),
  };
  localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(session));
  return session;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [user, setUser] = useState<AuthState['user']>(null);
  const [token, setToken] = useState<string | null>(null);

  function applySession(session: StoredSession | null) {
    if (!session) {
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      return;
    }
    setUser(session.claims);
    setToken(session.access_token);
    setIsAuthenticated(true);
  }

  useEffect(() => {
    applySession(loadStoredSession());
    setIsLoading(false);
  }, []);

  function refreshSession() {
    setAuthError(null);
    applySession(loadStoredSession());
  }

  async function login() {
    try {
      const { ztrustIssuerUrl, ztrustClientId } = getRuntimeConfig();
      const codeVerifier = randomString(32);
      const state = randomString(16);
      const codeChallenge = await sha256Base64Url(codeVerifier);

      sessionStorage.setItem(STORAGE_KEY_PKCE, JSON.stringify({ codeVerifier, state }));

      const redirectUri = `${window.location.origin}/callback`;
      const params = new URLSearchParams({
        client_id: ztrustClientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid profile email',
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      });
      window.location.assign(`${ztrustIssuerUrl}/protocol/openid-connect/auth?${params.toString()}`);
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Sign-in could not be started.');
    }
  }

  function logout() {
    localStorage.removeItem(STORAGE_KEY_SESSION);
    sessionStorage.removeItem(STORAGE_KEY_PKCE);
    setIsAuthenticated(false);
    setAuthError(null);
    setUser(null);
    setToken(null);

    const { ztrustIssuerUrl, ztrustClientId } = getRuntimeConfig();
    const params = new URLSearchParams({
      client_id: ztrustClientId,
      post_logout_redirect_uri: `${window.location.origin}/`,
    });
    // Keycloak's end_session endpoint (not the token endpoint) --
    // independently confirmed earlier this session to return correct
    // CORS headers, and a full-page navigation is not subject to CORS
    // regardless. Real network failures here just mean the user keeps
    // a live Keycloak SSO session after logging out of this app --
    // local session state above is already cleared either way.
    window.location.assign(`${ztrustIssuerUrl}/protocol/openid-connect/logout?${params.toString()}`);
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, authError, user, token, login, logout, refreshSession, setAuthError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/** Completes the authorization-code+PKCE redirect on /callback: reads
 * the code_verifier this same browser generated in login() (matched
 * by state, guarding against a stale/replayed callback), and calls
 * auth_token_proxy -- NOT Keycloak's /token endpoint directly -- for
 * the actual exchange. See this module's own top docstring for why.
 *
 * Guarded by a module-level flag against a double-fire within the
 * same page load (e.g. a StrictMode double-invoke in development);
 * a full page reload of /callback with a stale code correctly fails
 * with invalid_grant from Keycloak either way, surfaced as authError. */
let callbackHandled = false;

export async function completeSigninRedirect(): Promise<StoredSession> {
  if (callbackHandled) {
    throw new Error('Sign-in callback already handled for this page load.');
  }
  callbackHandled = true;

  const url = new URL(window.location.href);
  const error = url.searchParams.get('error');
  if (error) {
    throw new Error(url.searchParams.get('error_description') || error);
  }
  const code = url.searchParams.get('code');
  const returnedState = url.searchParams.get('state');
  if (!code || !returnedState) {
    throw new Error('Callback URL is missing the expected code/state parameters.');
  }

  const storedRaw = sessionStorage.getItem(STORAGE_KEY_PKCE);
  sessionStorage.removeItem(STORAGE_KEY_PKCE);
  if (!storedRaw) {
    throw new Error('No pending sign-in found for this browser session (state storage was empty).');
  }
  const { codeVerifier, state: expectedState } = JSON.parse(storedRaw) as { codeVerifier: string; state: string };
  if (returnedState !== expectedState) {
    throw new Error('Sign-in state mismatch -- possible replay or a stale callback URL.');
  }

  const { apiBaseUrl } = getRuntimeConfig();
  const response = await fetch(`${apiBaseUrl}/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      code,
      redirect_uri: `${window.location.origin}/callback`,
      code_verifier: codeVerifier,
    }),
  });
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body.error_description || body.error || `Token exchange failed (${response.status})`);
  }

  return storeSession(body);
}
